import { useState } from "react";
import { Link } from "react-router-dom";
import { Youtube, Instagram, Facebook, Twitter, Send, AtSign } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Header";
import { api, errMsg } from "@/lib/api";

const ICONS = { youtube: Youtube, instagram: Instagram, facebook: Facebook, x: Twitter, telegram: Send, threads: AtSign };

export function SocialLinks({ className = "" }) {
  const { settings } = useAuth();
  const social = Object.entries(settings.social || {}).filter(([, v]) => v);
  if (!social.length) return null;
  return (
    <div className={`flex gap-3 ${className}`} data-testid="social-strip">
      {social.map(([k, v]) => {
        const I = ICONS[k] || AtSign;
        return <a key={k} href={v} target="_blank" rel="noopener noreferrer" aria-label={k} data-testid={`social-${k}`} className="rounded-full border border-white/10 p-2.5 hover:border-amber-400 hover:text-amber-400 transition-colors"><I className="h-4 w-4" /></a>;
      })}
    </div>
  );
}

export function Newsletter() {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/newsletter", { email, consent }); toast.success("You're subscribed. Better movies, coming your way."); setEmail(""); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="newsletter-form">
      <div className="flex flex-col gap-2 sm:flex-row">
        <input data-testid="newsletter-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com"
          className="flex-1 rounded-full border border-white/10 bg-white/5 px-4 py-3 text-sm outline-none focus:border-amber-400" />
        <button data-testid="newsletter-submit" disabled={busy} className="rounded-full bg-purple-600 px-6 py-3 text-sm font-bold hover:bg-purple-500 disabled:opacity-60">Subscribe</button>
      </div>
      <label className="flex items-start gap-2 text-xs text-slate-400">
        <input data-testid="newsletter-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-amber-400" />
        I agree to receive occasional recommendation emails. Unsubscribe anytime.
      </label>
    </form>
  );
}

export function Footer() {
  const { settings } = useAuth();
  const social = Object.entries(settings.social || {}).filter(([, v]) => v);
  const legal = [["About Us", "/about"], ["Contact", "/contact"], ["Privacy Policy", "/page/privacy"], ["Terms of Service", "/page/terms"],
    ["Community Guidelines", "/page/community-guidelines"], ["Affiliate Disclosure", "/page/affiliate-disclosure"],
    ["Copyright & Takedown", "/page/copyright"], ["Account Deletion", "/page/account-deletion"]];
  return (
    <footer className="mt-20 border-t border-white/5 bg-[#08080B]" data-testid="site-footer">
      <div className="grid gap-12 px-4 py-14 sm:px-6 lg:grid-cols-[1.3fr_1fr_1fr] lg:px-12">
        <div>
          <Logo />
          <p className="mt-4 font-display text-3xl text-slate-100">{settings.brand_message}</p>
          <p className="mt-1 text-sm text-slate-500">{settings.tagline}</p>
          <div className="mt-6 flex gap-3">
            {social.length ? social.map(([k, v]) => {
              const I = ICONS[k] || AtSign;
              return <a key={k} href={v} target="_blank" rel="noopener noreferrer" aria-label={k} data-testid={`social-${k}`} className="rounded-full border border-white/10 p-2.5 hover:border-amber-400 hover:text-amber-400 transition-colors"><I className="h-4 w-4" /></a>;
            }) : <p className="text-xs text-slate-600">Social links coming soon.</p>}
          </div>
        </div>
        <div>
          <p className="eyebrow mb-4">Explore</p>
          <ul className="grid grid-cols-2 gap-2 text-sm text-slate-400 lg:grid-cols-1">
            {legal.map(([l, to]) => <li key={to}><Link to={to} className="hover:text-amber-400">{l}</Link></li>)}
          </ul>
        </div>
        <div>
          <p className="eyebrow mb-4">Newsletter</p>
          <p className="mb-4 text-sm text-slate-400">One email when Shivam drops new recommendations. No spam.</p>
          <Newsletter />
          <SocialLinks className="mt-6" />
        </div>
      </div>
      <div className="flex flex-col gap-2 border-t border-white/5 px-4 py-6 text-xs text-slate-600 sm:flex-row sm:justify-between sm:px-6 lg:px-12">
        <p>© {new Date().getFullYear()} {settings.site_name}. We never host or link to pirated content.</p>
        <p>Movie metadata & images from <a href="https://www.themoviedb.org" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-amber-400">TMDB</a>. This product uses the TMDB API but is not endorsed or certified by TMDB.</p>
      </div>
    </footer>
  );
}
